/**
 * One credential submission at a time, and everything about it that is the same
 * whatever is asking.
 *
 * ── Why this is not part of the view model that uses it ──────────────────────
 *
 * `SessionBackingViewModel` is the **web's** sign-in model, and it is web-shaped in a
 * way that will not survive contact with a phone: on the web, being signed out gates
 * the whole app behind a landing surface, the session travels as a cookie the browser
 * sends on its own, and there is no server address because the bundle talks to its own
 * origin. On Android and iOS none of that is true — the app is usable with no server at
 * all, and "signing in" is one step of configuring *syncing*, alongside a host and port
 * the user types and a session id the device has to keep. A single view model spanning
 * both would be a model with two thirds of itself switched off on each platform.
 *
 * What is genuinely identical is everything in this file, and it is more than it looks:
 *
 *  - the in-flight machine — idle → submitting → failed, and back;
 *  - the guard that a second submit while one is in flight does nothing;
 *  - trimming the username and refusing an empty half;
 *  - turning whatever the request threw into one sentence a person can read.
 *
 * That last one is the reason this file exists rather than the four rules living inline
 * where they are used. Written twice, the two clients eventually disagree about what a
 * 429 means, and the disagreement surfaces as one platform showing a throttle as a bad
 * password. §10 of docs/kmp-sharing-architecture.html gives the test to apply — not
 * "can this be shared?" but "would both platforms want this to change at the same time,
 * in the same way?" — and a change to any of the four above is a change both would want.
 *
 * **One injected function, and no more of an abstraction than that.** This class used to
 * take the concrete [StorageRepository], on the grounds that an abstraction with one
 * implementation is a hypothesis somebody has to maintain. The second implementation has
 * now arrived and it is not a repository at all, so the hypothesis is settled rather than
 * still open:
 *
 *  - the **web** submits through [StorageRepository.signIn], which replaces the session and
 *    the settings cache together, against the origin that served the bundle;
 *  - **mobile** submits against a server whose address the user has just typed, and then
 *    stores the session id the server hands back — see
 *    [se.soderbjorn.lunapin.client.viewmodel.SyncSetupBackingViewModel]. There is no
 *    repository in that path and no cookie jar underneath it.
 *
 * What both want from this class is identical, and it is exactly the four rules above. So
 * the seam is a single suspend function rather than an interface, a type parameter or a
 * second class: the caller supplies "send these two strings and throw if that failed", and
 * everything else stays here.
 *
 * @see StorageRepository
 * @see se.soderbjorn.lunapin.client.viewmodel.SessionBackingViewModel
 * @see se.soderbjorn.lunapin.client.viewmodel.SyncSetupBackingViewModel
 */
package se.soderbjorn.lunapin.client

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import se.soderbjorn.lunapin.clientserver.ApiFailure

/**
 * Submits a username and password, and says what is happening to it.
 *
 * Not a view model: it has no `run()`, it publishes no render-ready screen, and it knows
 * nothing about being signed out. A view model composes one of these with whatever else
 * its screen needs — for the web, the session itself.
 *
 * @param submitCredential sends one username and password to a server, and **throws when
 *   that failed** — an [ApiFailure] carrying the server's own sentence, or whatever the
 *   transport threw when there was no answer at all. Its return is ignored, because success
 *   is not this class's news to report: on the web it arrives through
 *   `StorageRepository.sessionFlow`, and on mobile through the stored connection. Both are
 *   places every interested party already reads.
 * @param scope where the round trip runs. Injected so a test can supply its own and
 *   await it, rather than racing a scope it cannot see.
 */
class SignInAttempt(
    private val submitCredential: suspend (username: String, password: String) -> Unit,
    private val scope: CoroutineScope,
) {
    private val _stateFlow = MutableStateFlow(State())

    /** What the current attempt is doing, or how the last one went. */
    val stateFlow: StateFlow<State> = _stateFlow.asStateFlow()

    /**
     * Which attempt the state is allowed to describe.
     *
     * Incremented by every [submit] and every [onDismissed], and checked before a finished
     * request writes anything. Without it, a request that was still open when the surface
     * was closed reports its refusal into a dialog that is no longer there — and the
     * message is then waiting, stale, on the next open. That is not hypothetical: the
     * server is reached over a network, and Cancel is exactly what somebody presses when a
     * request is taking a while.
     *
     * A counter rather than cancelling the request, because cancelling is the wrong
     * behaviour: the server may already have accepted the credential and minted a session,
     * and a client that had thrown away the response would be signed in while believing it
     * was not. A late success still lands, through `sessionFlow`, which is where it belongs.
     */
    private var generation: Int = 0

    /**
     * Send [username] and [password] to the server.
     *
     * Does not suspend, and does not report success: success *is* a new session, and it
     * arrives where every other interested party already reads it — `sessionFlow` on the
     * web, the stored connection on mobile. A completion handler here would be a second way
     * to learn the same thing, and on Swift it would be a completion handler at every call
     * site (§05 of the architecture doc).
     *
     * Two things are refused before anything is sent, and then the last attempt's message
     * is cleared as the new one opens:
     *
     *  1. **Already in flight.** A second submit does nothing at all. The surfaces also
     *     guard this by replacing the button while it is busy, and that is not enough on
     *     its own — Enter still reaches a form whose button has gone.
     *  2. **A blank half.** Refused silently, because a surface that offers a live
     *     button over an empty field is the bug, and reporting "fill this in" from here
     *     would be this class writing UI copy for a state the UI should not have
     *     produced.
     *
     * @param username trimmed before it is sent. Somebody who has copied their username
     *   out of a password manager has very often copied a trailing space with it, and
     *   the server compares byte for byte.
     * @param password sent **exactly** as given. Never trimmed: a space is a legal
     *   character in a password, and silently deleting one would refuse a correct
     *   credential with the server's "not accepted" sentence — which is the single most
     *   confusing failure this form can produce. Not logged, not stored, and not kept
     *   after this call; the session is the credential from here on — a cookie in the
     *   browser, a stored session id on a phone.
     */
    fun submit(username: String, password: String) {
        if (_stateFlow.value.isSubmitting) return
        val trimmedUsername = username.trim()
        if (trimmedUsername.isEmpty() || password.isEmpty()) return
        val mine = ++generation
        _stateFlow.value = State(isSubmitting = true)
        scope.launch {
            runCatching { submitCredential(trimmedUsername, password) }
                // Nothing to report on success. The state goes back to idle so that a
                // surface still on screen — the web's dialog is, for the beat before the
                // page reloads — stops showing a spinner.
                .onSuccess { if (mine == generation) _stateFlow.value = State() }
                .onFailure { cause ->
                    if (mine == generation) {
                        _stateFlow.value = State(errorMessage = cause.readableMessage(SIGN_IN_FAILED))
                    }
                }
        }
    }

    /**
     * The user changed one of the two fields.
     *
     * Drops the failure message, because it is now describing something other than what
     * is in the boxes. A message that outlives its cause is worse than no message: it
     * reads as a rejection of what has just been typed, so the next thing the user does
     * is stop typing and reread a sentence about the previous attempt.
     *
     * A no-op mid-flight, which falls out of the two fields never being set together —
     * see [State].
     */
    fun onCredentialEdited() {
        if (_stateFlow.value.errorMessage == null) return
        _stateFlow.value = State()
    }

    /**
     * The user closed the sign-in surface.
     *
     * Back to idle, so reopening it does not present the last attempt's failure as if it
     * were about this one — including a failure that had not arrived yet when the surface
     * closed. See [generation], which is what makes that second half true.
     */
    fun onDismissed() {
        generation++
        _stateFlow.value = State()
    }

    /**
     * Exactly one of these two is ever interesting, and they are never both set.
     *
     * Kept as a small pair rather than the one-non-null-field style the published screen
     * states use, because nothing renders this directly — it is an input to a view
     * model, and the invariant is enforced by every assignment in this file constructing
     * a whole [State].
     *
     * @property isSubmitting a round trip is open.
     * @property errorMessage what the last attempt failed with, in words, or null.
     */
    data class State(
        val isSubmitting: Boolean = false,
        val errorMessage: String? = null,
    )

    private companion object {
        /**
         * Used only when the server refused without saying anything, which no route of
         * ours does. It exists so that a proxy returning a bare 502 cannot produce an
         * empty error banner.
         */
        const val SIGN_IN_FAILED = "That sign-in did not work. Please try again."
    }
}

/**
 * One sentence for a person, out of whatever a request threw.
 *
 * **The server's own sentence, whenever there is one — and there is no `when` on the
 * status here.** That absence is the design, so it is worth stating what it replaces.
 * The three refusals this project can produce all arrive already written for a reader:
 *
 *  - **401** — "That username and password combination was not accepted." Deliberately
 *    non-disclosing, because the server compares both halves unconditionally and so
 *    genuinely does not know which was wrong (see the server's `AuthConfig.matches`). A
 *    client that worded this itself would be a client one edit away from "check your
 *    username", which turns one guessing problem into two easier ones.
 *  - **429** — "Too many sign-in attempts. Try again in N seconds." The wait is *in the
 *    sentence*, which is why nothing here reads the `Retry-After` header: that header is
 *    the machine-readable half, for a client that retries on its own, and nothing here
 *    retries on its own — an automatic retry against a throttle is how a caller stays
 *    throttled. [ApiFailure] carries no headers for the same reason.
 *  - **503** — "Sign-in is not configured on this server." Reachable when a server was
 *    restarted without its variables between the session response and the submit, which
 *    is rare and confusing enough to deserve the operator's own wording rather than a
 *    guess.
 *
 * A three-branch `when` here would be a second copy of all three, free to drift from the
 * server's, and drift is invisible: both versions are plausible sentences.
 *
 * @param fallback what to say when the server refused *without* a message. Not a default
 *   — each caller's is different, and a shared one would be a sentence about signing in
 *   appearing after a failed sign-*out*.
 */
internal fun Throwable.readableMessage(fallback: String): String {
    // Not an ApiFailure at all: the request never got an answer. The only case this side
    // knows more about than the server does, because the server said nothing.
    val failure = this as? ApiFailure
        ?: return "Could not reach the LunaPin server. Please try again."
    return failure.serverMessage.takeIf { it.isNotBlank() } ?: fallback
}
